import posthog from 'posthog-js';
import { isNative } from './platformUtils';

/**
 * Fire-and-forget analytics wrapper around PostHog.
 * Every public function silently swallows errors so analytics
 * can never crash or slow the app.
 */

// Defer work off the interaction path: posthog.capture writes synchronously to
// localStorage (session state), which is main-thread I/O we don't want inside
// a tap or an audio callback.
function deferred(fn) {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(fn, { timeout: 2000 });
  } else {
    setTimeout(fn, 0);
  }
}

/** Send a named event with optional properties. */
export function track(eventName, properties, { sendBeacon = false } = {}) {
  try {
    if (sendBeacon) {
      // Unload-path events must fire now or never
      posthog.capture(eventName, properties, { transport: 'sendBeacon' });
      return;
    }
    deferred(() => {
      try {
        posthog.capture(eventName, properties);
      } catch (_) { /* silent */ }
    });
  } catch (_) { /* silent */ }
}

// Premier lancement de l'app installee, une fois par installation. Le drapeau
// vit dans le stockage local du webview : desinstaller l'app l'emporte, ce qui
// est exactement la definition voulue.
//
// Point de mesure de la derniere marche du tunnel d'invitation : le code est
// rachete sur le web, l'installation se joue ailleurs, et rien ne disait
// jusqu'ici combien d'invites arrivaient vraiment jusqu'a l'app.
const NATIVE_FIRST_OPEN_KEY = 'upscale_native_first_open';

export function trackNativeFirstOpen() {
  try {
    if (!isNative()) return;
    if (localStorage.getItem(NATIVE_FIRST_OPEN_KEY)) return;
    localStorage.setItem(NATIVE_FIRST_OPEN_KEY, new Date().toISOString());
    track('native_first_open');
  } catch (_) { /* silent */ }
}

/** Identify a user (for future auth). */
export function identify(userId, traits) {
  try {
    posthog.identify(userId, traits);
  } catch (_) { /* silent */ }
}

/** Merge person properties (overwrites existing keys). */
export function setUserProperties(props) {
  try {
    posthog.people.set(props);
  } catch (_) { /* silent */ }
}

/** Set person properties only if they haven't been set before. */
export function setUserPropertiesOnce(props) {
  try {
    posthog.people.set_once(props);
  } catch (_) { /* silent */ }
}

/** Reset analytics identity (e.g. on "clear data" in Settings). */
export function resetAnalytics() {
  try {
    posthog.reset();
  } catch (_) { /* silent */ }
}

/** Force a new session ID so the next events start a fresh session. */
export function resetSessionId() {
  try {
    posthog.reset_session_id();
  } catch (_) { /* silent */ }
}

/**
 * Called once at startup to batch device-level person properties.
 * Sets them via set_once so they only record the first session values.
 */
export function setDeviceContext() {
  try {
    setUserPropertiesOnce({
      platform: isNative() ? 'native' : 'web',
      is_native: isNative(),
      app_version: '0.91',
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
      pixel_ratio: window.devicePixelRatio,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      locale: navigator.language,
    });
  } catch (_) { /* silent */ }
}
